import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, Edit2 } from "lucide-react";

const TABS = ["overview", "notes", "meetings"];

export default function AdminClientDetail() {
  const { user_id } = useParams();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);

  const load = async () => {
    try { setData((await api.get(`/admin/clients/${user_id}`)).data); }
    catch { toast.error("Could not load client"); }
  };
  useEffect(() => { load(); }, [user_id]);

  if (!data) return <div className="text-ink-soft text-sm">Loading client…</div>;
  const u = data.user;

  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-stone hover:text-ivy">
        <ChevronLeft className="h-3 w-3" /> All clients
      </Link>

      <header className="border-b border-stone/30 pb-5">
        <h1 className="font-serif text-3xl text-ink tracking-tight">{u.name || u.email}</h1>
        <div className="flex flex-wrap items-baseline gap-4 mt-2 text-xs">
          <span className="font-mono text-ink-soft">{u.email}</span>
          <span className="inline-block border border-stone/40 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-ivy" data-testid="client-tier-pill">{u.tier || (u.audit_pending ? "audit-only" : "none")}</span>
          {u.risk_level && <span className="text-[10px] uppercase tracking-[0.18em] text-brass">Risk: {u.risk_level}</span>}
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-stone/30 flex gap-6" data-testid="client-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`pb-3 text-[11px] uppercase tracking-[0.22em] border-b-2 ${tab === t ? "text-ivy border-ivy font-semibold" : "text-stone border-transparent hover:text-ink"}`}
            data-testid={`client-tab-${t}`}>
            {t === "overview" ? "Overview" : t === "notes" ? "Advisor notes" : "Meetings"}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab data={data} onSaved={load} />}
      {tab === "notes" && <NotesTab data={data} onChanged={load} />}
      {tab === "meetings" && <MeetingsTab data={data} onChanged={load} />}
    </div>
  );
}

function OverviewTab({ data, onSaved }) {
  const u = data.user;
  const [form, setForm] = useState({
    tier: u.tier || "",
    target_schools: (u.target_schools || []).join(", "),
    intended_major: u.intended_major || "",
    current_college: u.current_college || "",
    status_label: u.status_label || "",
    risk_level: u.risk_level || "",
  });
  const save = async (field) => {
    try {
      const payload = {};
      if (field === "target_schools") payload.target_schools = form.target_schools.split(",").map((s) => s.trim()).filter(Boolean);
      else payload[field] = form[field] || null;
      await api.patch(`/admin/clients/${u.user_id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-7" data-testid="overview-tab">
      {u.is_exemplar && (
        <div className="border border-brass bg-bone p-5 relative" data-testid="exemplar-callout">
          <span className="absolute -top-2 left-4 bg-brass text-bone text-[9px] uppercase tracking-[0.22em] font-semibold px-2 py-0.5">
            ◆ Exemplar profile
          </span>
          <p className="mt-1 text-ivy text-sm leading-relaxed">{u.exemplar_summary}</p>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-7">
      <div className="lg:col-span-7 space-y-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Profile (autosave on blur)</div>
        <Field label="Tier (comp)" value={form.tier} onChange={(v) => setForm({ ...form, tier: v })} onBlur={() => save("tier")} testid="field-tier" type="select"
          options={[["", "— none —"], ["platform", "Platform"], ["florida_transfer", "Florida Transfer"], ["selective_elite", "Selective & Elite"]]} />
        <Field label="Target schools (comma)" value={form.target_schools} onChange={(v) => setForm({ ...form, target_schools: v })} onBlur={() => save("target_schools")} testid="field-targets" />
        <Field label="Intended major" value={form.intended_major} onChange={(v) => setForm({ ...form, intended_major: v })} onBlur={() => save("intended_major")} testid="field-major" />
        <Field label="Current college" value={form.current_college} onChange={(v) => setForm({ ...form, current_college: v })} onBlur={() => save("current_college")} testid="field-college" />
        <Field label="Status label" value={form.status_label} onChange={(v) => setForm({ ...form, status_label: v })} onBlur={() => save("status_label")} testid="field-status" />
        <Field label="Risk level" value={form.risk_level} onChange={(v) => setForm({ ...form, risk_level: v })} onBlur={() => save("risk_level")} testid="field-risk" type="select"
          options={[["", "— none —"], ["green", "Green"], ["warn", "Warn"], ["risk", "Risk"]]} />
      </div>

      <div className="lg:col-span-5 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Activity</div>
        <ActivityTimeline data={data} />
      </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, onBlur, type, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-stone">{label}</label>
      {type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
          className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid={testid}>
          {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur}
          className="mt-2 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid={testid} />
      )}
    </div>
  );
}

function ActivityTimeline({ data }) {
  // Merge tool_runs + audits + notes + meetings by date desc, take 12.
  const items = [
    ...data.tool_runs.map((r) => ({ kind: "tool", date: r.created_at, label: r.tool, sub: r.email || r.user_id })),
    ...data.transcript_audits.map((a) => ({ kind: "audit", date: a.created_at, label: "Transcript audit", sub: a.intended_major || "" })),
    ...data.advisor_notes.map((n) => ({ kind: "note", date: n.created_at, label: n.title, sub: n.author_name || "admin" })),
    ...data.meetings.map((m) => ({ kind: "meeting", date: m.date, label: "Meeting · " + (m.duration_minutes || 60) + "m", sub: (m.summary || "").slice(0, 80) })),
  ].filter(x => x.date).sort((a, b) => (b.date > a.date ? 1 : -1)).slice(0, 12);
  if (items.length === 0) return <p className="text-sm text-ink-soft">No activity yet.</p>;
  return (
    <ul className="border-t border-l border-stone/40">
      {items.map((it, i) => (
        <li key={i} className="border-r border-b border-stone/40 bg-bone px-3 py-2.5 flex items-baseline gap-3">
          <span className="text-[9px] uppercase tracking-[0.18em] text-brass w-14 mt-0.5">{it.kind}</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-ink truncate">{it.label}</div>
            {it.sub && <div className="text-[10px] text-ink-soft truncate font-mono">{it.sub}</div>}
          </div>
          <span className="text-[10px] text-stone font-mono tabular-nums">{(it.date || "").slice(0, 10)}</span>
        </li>
      ))}
    </ul>
  );
}

function NotesTab({ data, onChanged }) {
  const u = data.user;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ title: "", body_markdown: "", visible_to_student: true });
  const [editing, setEditing] = useState(null);

  const save = async () => {
    if (!draft.title || !draft.body_markdown) { toast.error("Title and body required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/clients/${u.user_id}/notes/${editing}`, draft);
        toast.success("Note updated");
      } else {
        await api.post(`/admin/clients/${u.user_id}/notes`, draft);
        toast.success("Note added");
      }
      setAdding(false); setEditing(null); setDraft({ title: "", body_markdown: "", visible_to_student: true });
      onChanged();
    } catch { toast.error("Save failed"); }
  };
  const del = async (note_id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/admin/clients/${u.user_id}/notes/${note_id}`); toast.success("Deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };
  const startEdit = (n) => { setDraft({ title: n.title, body_markdown: n.body_markdown, visible_to_student: n.visible_to_student }); setEditing(n.note_id); setAdding(true); };

  return (
    <div className="space-y-5" data-testid="notes-tab">
      {!adding && (
        <button onClick={() => { setAdding(true); setEditing(null); setDraft({ title: "", body_markdown: "", visible_to_student: true }); }}
          className="inline-flex items-center gap-2 border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5"
          data-testid="add-note-btn">
          <Plus className="h-3.5 w-3.5" /> Add note
        </button>
      )}

      {adding && (
        <div className="border border-stone/40 bg-bone-dim p-5" data-testid="note-form">
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Note title" className="w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="note-title" />
          <div className="grid lg:grid-cols-2 gap-3 mt-3">
            <textarea value={draft.body_markdown} onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
              rows={10} placeholder="Markdown body…"
              className="w-full bg-bone border border-stone/40 px-3 py-2 text-sm font-mono" data-testid="note-body" />
            <div className="border border-stone/40 bg-bone px-3 py-2 prose prose-sm max-w-none overflow-auto" data-testid="note-preview">
              <ReactMarkdown>{draft.body_markdown || "*Preview will appear here…*"}</ReactMarkdown>
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs text-ink-soft">
            <input type="checkbox" checked={draft.visible_to_student} onChange={(e) => setDraft({ ...draft, visible_to_student: e.target.checked })} data-testid="note-visibility" />
            Visible to student
          </label>
          <div className="mt-4 flex gap-3">
            <button onClick={save} className="border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5" data-testid="note-save">{editing ? "Update" : "Save"}</button>
            <button onClick={() => { setAdding(false); setEditing(null); }} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ink">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {data.advisor_notes.length === 0 && <p className="text-sm text-ink-soft">No notes yet.</p>}
        {data.advisor_notes.map((n) => (
          <article key={n.note_id} className="border border-stone/40 bg-bone p-5" data-testid={`note-${n.note_id}`}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-serif text-lg text-ink tracking-tight">{n.title}</h3>
                <div className="text-[10px] uppercase tracking-[0.18em] text-stone mt-1">
                  {n.author_name || "admin"} · {(n.created_at || "").slice(0, 10)}
                  {!n.visible_to_student && <span className="text-brass ml-3">Internal</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(n)} className="border border-stone/40 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-ink-soft hover:bg-stone/10" data-testid={`note-edit-${n.note_id}`}><Edit2 className="h-3 w-3" /></button>
                <button onClick={() => del(n.note_id)} className="border border-stone/40 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-ink-soft hover:bg-stone/10" data-testid={`note-delete-${n.note_id}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
            <div className="mt-3 prose prose-sm max-w-none text-ink leading-relaxed">
              <ReactMarkdown>{n.body_markdown}</ReactMarkdown>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function MeetingsTab({ data, onChanged }) {
  const u = data.user;
  const [adding, setAdding] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [draft, setDraft] = useState({ date: today, duration_minutes: 60, summary: "", action_items: [""], recording_url: "" });

  const save = async () => {
    if (!draft.summary) { toast.error("Summary required"); return; }
    try {
      await api.post(`/admin/clients/${u.user_id}/meetings`, {
        ...draft, action_items: draft.action_items.filter((a) => a.trim()),
        recording_url: draft.recording_url || null,
      });
      toast.success("Meeting logged");
      setAdding(false); setDraft({ date: today, duration_minutes: 60, summary: "", action_items: [""], recording_url: "" });
      onChanged();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-5" data-testid="meetings-tab">
      {!adding && (
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5" data-testid="add-meeting-btn">
          <Plus className="h-3.5 w-3.5" /> Log meeting
        </button>
      )}
      {adding && (
        <div className="border border-stone/40 bg-bone-dim p-5 space-y-3" data-testid="meeting-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-xs">Date<input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="mt-1 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="meeting-date" /></label>
            <label className="text-xs">Duration (min)<input type="number" value={draft.duration_minutes} onChange={(e) => setDraft({ ...draft, duration_minutes: Number(e.target.value) || 60 })} className="mt-1 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="meeting-duration" /></label>
            <label className="text-xs">Recording URL<input value={draft.recording_url} onChange={(e) => setDraft({ ...draft, recording_url: e.target.value })} className="mt-1 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="meeting-url" /></label>
          </div>
          <label className="text-xs block">Summary<textarea value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={5} className="mt-1 w-full bg-bone border border-stone/40 px-3 py-2 text-sm" data-testid="meeting-summary" /></label>
          <div>
            <div className="text-xs mb-1">Action items</div>
            {draft.action_items.map((a, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input value={a} onChange={(e) => { const arr = [...draft.action_items]; arr[i] = e.target.value; setDraft({ ...draft, action_items: arr }); }}
                  className="flex-1 bg-bone border border-stone/40 px-3 py-1.5 text-sm" data-testid={`meeting-action-${i}`} />
                <button onClick={() => setDraft({ ...draft, action_items: draft.action_items.filter((_, j) => j !== i) })} className="border border-stone/40 px-2 text-stone hover:text-ink"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
            <button onClick={() => setDraft({ ...draft, action_items: [...draft.action_items, ""] })} className="text-[10px] uppercase tracking-[0.22em] text-ivy hover:text-brass">+ Add action item</button>
          </div>
          <div className="flex gap-3">
            <button onClick={save} className="border border-ivy bg-ivy text-bone uppercase tracking-[0.18em] text-[11px] font-semibold py-2.5 px-5" data-testid="meeting-save">Save</button>
            <button onClick={() => setAdding(false)} className="text-[11px] uppercase tracking-[0.22em] text-stone hover:text-ink">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {data.meetings.length === 0 && <p className="text-sm text-ink-soft">No meetings logged.</p>}
        {data.meetings.map((m) => (
          <details key={m.meeting_id} className="border border-stone/40 bg-bone p-5" data-testid={`meeting-${m.meeting_id}`}>
            <summary className="cursor-pointer flex items-baseline justify-between gap-3">
              <div>
                <span className="font-mono text-sm text-ink">{m.date}</span>
                <span className="ml-3 text-[10px] uppercase tracking-[0.18em] text-brass">{m.duration_minutes}m</span>
              </div>
              <span className="text-xs text-ink-soft truncate max-w-md">{(m.summary || "").slice(0, 100)}</span>
            </summary>
            <div className="mt-3 text-sm text-ink-soft leading-relaxed whitespace-pre-wrap">{m.summary}</div>
            {m.action_items?.length > 0 && (
              <ul className="mt-3 list-disc pl-5 text-sm text-ink-soft">{m.action_items.map((a, i) => <li key={i}>{a}</li>)}</ul>
            )}
            {m.recording_url && <a href={m.recording_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[10px] uppercase tracking-[0.22em] text-ivy">Recording →</a>}
          </details>
        ))}
      </div>
    </div>
  );
}
